import React, { useState, useRef, useEffect } from 'react';
import { Trash2, ChevronUp, ChevronDown, Plus, X, Search } from 'lucide-react';
import { API_BASE_URL } from '../config';

const FIELD_TYPES = [
  { type: 'text', label: 'Text', icon: 'Aa' },
  { type: 'number', label: 'Number', icon: '#' },
  { type: 'email', label: 'Email', icon: '@' },
  { type: 'date', label: 'Date', icon: '📅' },
  { type: 'time', label: 'Time', icon: '🕐' },
  { type: 'datetime', label: 'Date & Time', icon: '🕒' },
  { type: 'textarea', label: 'Long Text', icon: '¶' },
  { type: 'select', label: 'Dropdown', icon: '▾' },
  { type: 'multiselect', label: 'Checkbox Group', icon: '☑' },
  { type: 'image', label: 'Image Upload', icon: '🖼' },
  { type: 'department_members', label: 'Department Members', icon: '👥' },
  { type: 'section', label: 'Section Breaker', icon: '—' },
];

export default function FormBuilder({ form, onChange, saveStatus }) {
  const fields = form.fields || [];
  const [selectedId, setSelectedId] = useState(null);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [addSearch, setAddSearch] = useState('');
  const [datasets, setDatasets] = useState([]);
  const [selectedDatasetMeta, setSelectedDatasetMeta] = useState(null);
  const addMenuRef = useRef(null);
  const builderRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${API_BASE_URL}/api/lookup/datasets`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : [])
      .then(data => setDatasets(data || []))
      .catch(() => setDatasets([]));
  }, []);

  useEffect(() => {
    const syncDatasetId = form.lookupSync?.datasetId;
    if (!syncDatasetId) { setSelectedDatasetMeta(null); return; }
    const token = localStorage.getItem('token');
    fetch(`${API_BASE_URL}/api/lookup/datasets/${encodeURIComponent(syncDatasetId)}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => setSelectedDatasetMeta(d))
      .catch(() => setSelectedDatasetMeta(null));
  }, [form.lookupSync?.datasetId]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (addMenuRef.current && !addMenuRef.current.contains(e.target)) {
        setShowAddMenu(false);
        setAddSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const updateField = (i, patch) => {
    const next = fields.map((f, idx) => idx === i ? { ...f, ...patch } : f);
    onChange({ ...form, fields: next });
  };

  const addField = (type) => {
    const id = String(Date.now() + Math.random());
    const label =
      type === 'department_members' ? 'Department Members'
      : type === 'section' ? 'Section'
      : type === 'datetime' ? 'Date & Time'
      : `New ${type} field`;
    const f = {
      id, label, type,
      options: (type === 'select' || type === 'multiselect') ? ['Option 1', 'Option 2'] : [],
      required: false, unique: false, placeholder: ''
    };
    onChange({ ...form, fields: [...fields, f] });
    setSelectedId(id);
    setShowAddMenu(false);
    setAddSearch('');
  };

  const removeField = (i) => {
    if (fields[i].id === selectedId) setSelectedId(null);
    onChange({ ...form, fields: fields.filter((_, idx) => idx !== i) });
  };

  const moveField = (i, dir) => {
    const ti = dir === 'up' ? i - 1 : i + 1;
    if (ti < 0 || ti >= fields.length) return;
    const next = [...fields];
    [next[i], next[ti]] = [next[ti], next[i]];
    onChange({ ...form, fields: next });
  };

  const filteredTypes = FIELD_TYPES.filter(t =>
    t.label.toLowerCase().includes(addSearch.toLowerCase())
  );

  const renderTooltip = (f, i) => (
    <div className="ib-tooltip" onClick={(e) => e.stopPropagation()}>
      <div className="ib-tooltip-arrow" />
      <div className="ib-tooltip-body">
        <div className="ib-tooltip-row">
          <label>Label</label>
          <input
            value={f.label || ''}
            onChange={(e) => updateField(i, { label: e.target.value })}
          />
        </div>
        {f.type !== 'section' && (
          <div className="ib-tooltip-row">
            <label>Placeholder</label>
            <input
              value={f.placeholder || ''}
              onChange={(e) => updateField(i, { placeholder: e.target.value })}
            />
          </div>
        )}
        {(f.type === 'select' || f.type === 'multiselect') && (
          <div className="ib-tooltip-row">
            <label>Options (comma-separated)</label>
            <input
              defaultValue={(f.options || []).join(', ')}
              onBlur={(e) => updateField(i, { options: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
            />
          </div>
        )}
        {f.type !== 'section' && (
          <div className="ib-tooltip-check">
            <input
              type="checkbox"
              id={`ib-req-${f.id}`}
              checked={f.required || false}
              onChange={(e) => updateField(i, { required: e.target.checked })}
            />
            <label htmlFor={`ib-req-${f.id}`}>Required</label>
          </div>
        )}
        {f.type !== 'section' && f.type !== 'image' && f.type !== 'department_members' && f.type !== 'multiselect' && (
          <div className="ib-tooltip-check">
            <input
              type="checkbox"
              id={`ib-uniq-${f.id}`}
              checked={f.unique || false}
              onChange={(e) => updateField(i, { unique: e.target.checked })}
            />
            <label htmlFor={`ib-uniq-${f.id}`}>Unique</label>
          </div>
        )}
        {(f.type === 'text' || f.type === 'textarea' || f.type === 'email') && (
          <div className="ib-tooltip-row">
            <label>Max characters</label>
            <input
              type="number"
              min="1"
              value={f.maxLength || ''}
              placeholder="No limit"
              onChange={(e) => {
                const raw = e.target.value.trim();
                if (!raw) {
                  const { maxLength, ...rest } = f;
                  onChange({ ...form, fields: fields.map((x, idx) => idx === i ? rest : x) });
                  return;
                }
                const parsed = Math.floor(Number(raw));
                if (Number.isFinite(parsed) && parsed > 0) updateField(i, { maxLength: parsed });
              }}
            />
          </div>
        )}
        {(f.type === 'text' || f.type === 'textarea' || f.type === 'number') && (
          <>
            <div className="ib-tooltip-row">
              <label>Lookup dataset</label>
              <select
                value={f.lookup?.datasetId || ''}
                onChange={(e) => {
                  const id = e.target.value;
                  if (!id) {
                    const { lookup, ...rest } = f;
                    onChange({ ...form, fields: fields.map((x, idx) => idx === i ? rest : x) });
                  } else {
                    updateField(i, { lookup: { ...(f.lookup || {}), datasetId: id, mode: f.lookup?.mode || 'warn' } });
                  }
                }}
              >
                <option value="">— none —</option>
                {datasets.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            {f.lookup?.datasetId && (
              <div className="ib-tooltip-row">
                <label>Lookup mode</label>
                <select
                  value={f.lookup?.mode || 'warn'}
                  onChange={(e) => updateField(i, { lookup: { ...(f.lookup || {}), mode: e.target.value } })}
                >
                  <option value="warn">Warn (show existing, allow continue)</option>
                  <option value="select">Select (pick from list, fill field)</option>
                </select>
              </div>
            )}
          </>
        )}
        <div className="ib-tooltip-type">Type: <strong>{f.type}</strong></div>
      </div>
    </div>
  );

  const renderFieldPreview = (f, i) => {
    const isSelected = f.id === selectedId;

    if (f.type === 'section') {
      return (
        <div
          key={f.id}
          className={`ib-field ${isSelected ? 'ib-field-selected' : ''}`}
          onClick={(e) => { e.stopPropagation(); setSelectedId(f.id); }}
        >
          <div className="ib-section-break">
            <div className="ib-section-line" />
            <span className="ib-section-label">{f.label || 'Section'}</span>
            <div className="ib-section-line" />
          </div>
          {isSelected && (
            <div className="ib-field-actions">
              <button className="ib-action-btn" onClick={(e) => { e.stopPropagation(); moveField(i, 'up'); }} disabled={i === 0}>
                <ChevronUp size={14} />
              </button>
              <button className="ib-action-btn" onClick={(e) => { e.stopPropagation(); moveField(i, 'down'); }} disabled={i === fields.length - 1}>
                <ChevronDown size={14} />
              </button>
              <button className="ib-action-btn ib-action-danger" onClick={(e) => { e.stopPropagation(); removeField(i); }}>
                <Trash2 size={14} />
              </button>
            </div>
          )}
          {isSelected && renderTooltip(f, i)}
        </div>
      );
    }

    return (
      <div
        key={f.id}
        className={`ib-field ${isSelected ? 'ib-field-selected' : ''}`}
        onClick={(e) => { e.stopPropagation(); setSelectedId(f.id); }}
      >
        <label className="ib-label">
          {f.label || 'Unlabeled'} {f.required && <span className="required-star">*</span>}
        </label>
        {f.type === 'text' && <input className="ib-input" placeholder={f.placeholder} readOnly />}
        {f.type === 'email' && <input className="ib-input" placeholder={f.placeholder || 'email@example.com'} readOnly />}
        {f.type === 'number' && <input className="ib-input" placeholder={f.placeholder || '0'} readOnly />}
        {f.type === 'date' && <input type="date" className="ib-input" readOnly />}
        {f.type === 'time' && <input type="time" className="ib-input" readOnly />}
        {/* Render datetime-local for combined date+time values */}
        {f.type === 'datetime' && <input type="datetime-local" className="ib-input" readOnly />}
        {f.type === 'textarea' && <textarea className="ib-textarea" placeholder={f.placeholder} rows={3} readOnly />}
        {f.type === 'select' && (
          <select className="ib-select" disabled>
            <option>Select...</option>
            {(f.options || []).map(o => <option key={o}>{o}</option>)}
          </select>
        )}
        {f.type === 'multiselect' && (
          <div className="ib-checkbox-group">
            {(f.options || []).map(o => (
              <div key={o} className="field-checkbox">
                <input type="checkbox" disabled />
                <label>{o}</label>
              </div>
            ))}
          </div>
        )}
        {f.type === 'image' && (
          <div className="ib-placeholder-block">Image upload area</div>
        )}
        {f.type === 'department_members' && (
          <div className="ib-placeholder-block">Department members (checkbox list)</div>
        )}
        {isSelected && (
          <div className="ib-field-actions">
            <button className="ib-action-btn" onClick={(e) => { e.stopPropagation(); moveField(i, 'up'); }} disabled={i === 0}>
              <ChevronUp size={14} />
            </button>
            <button className="ib-action-btn" onClick={(e) => { e.stopPropagation(); moveField(i, 'down'); }} disabled={i === fields.length - 1}>
              <ChevronDown size={14} />
            </button>
            <button className="ib-action-btn ib-action-danger" onClick={(e) => { e.stopPropagation(); removeField(i); }}>
              <Trash2 size={14} />
            </button>
          </div>
        )}
        {isSelected && renderTooltip(f, i)}
      </div>
    );
  };

  return (
    <div className="ib-wrapper">
      <div className="ib-canvas" ref={builderRef} onClick={() => setSelectedId(null)}>
        <div className="ib-form-card">
          <div className="ib-form-header" onClick={(e) => e.stopPropagation()}>
            <input
              className="ib-form-title-input"
              value={form.name || ''}
              onChange={(e) => onChange({ ...form, name: e.target.value })}
              placeholder="Untitled Form"
            />
            <input
              className="ib-form-desc-input"
              value={form.description || ''}
              onChange={(e) => onChange({ ...form, description: e.target.value })}
              placeholder="Add a description..."
            />
          </div>

          <div className="ib-fields-area">
            {fields.length === 0 && (
              <div className="ib-empty">
                <p>Click the <strong>+</strong> button below to add your first field</p>
              </div>
            )}
            {fields.map((f, i) => renderFieldPreview(f, i))}
          </div>

          <LookupSyncPanel
            form={form}
            onChange={onChange}
            datasets={datasets}
            datasetMeta={selectedDatasetMeta}
            fields={fields}
          />

          <div className="ib-add-row" ref={addMenuRef}>
            <button
              className={`ib-add-btn ${showAddMenu ? 'ib-add-btn-active' : ''}`}
              onClick={(e) => { e.stopPropagation(); setShowAddMenu(v => !v); setAddSearch(''); }}
            >
              {showAddMenu ? <X size={20} /> : <Plus size={20} />}
            </button>
            {showAddMenu && (
              <div className="ib-add-menu" onClick={(e) => e.stopPropagation()}>
                <div className="ib-add-search-wrap">
                  <Search size={14} className="ib-add-search-icon" />
                  <input
                    className="ib-add-search"
                    placeholder="Search field types..."
                    value={addSearch}
                    onChange={(e) => setAddSearch(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="ib-add-list">
                  {filteredTypes.map(t => (
                    <button key={t.type} className="ib-add-item" onClick={() => addField(t.type)}>
                      <span className="ib-add-item-icon">{t.icon}</span>
                      <span>{t.label}</span>
                    </button>
                  ))}
                  {filteredTypes.length === 0 && (
                    <div className="ib-add-empty">No matching types</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
{/*
      <div className="ib-bottom-bar">
        <span className="ib-save-status">
          {saveStatus === 'saving' && 'Saving...'}
          {saveStatus === 'saved' && '✓ Saved'}
          {saveStatus === 'unsaved' && 'Unsaved changes'}
        </span>
      </div> */}
    </div>
  );
}

function LookupSyncPanel({ form, onChange, datasets, datasetMeta, fields }) {
  const sync = form.lookupSync || null;
  const enabled = !!sync && !!sync.datasetId;
  const fieldOptions = (fields || []).filter(f => f.type !== 'section');

  const update = (patch) => onChange({ ...form, lookupSync: { ...(sync || {}), ...patch } });
  const clear = () => onChange({ ...form, lookupSync: null });

  const datasetKeys = [
    ...new Set([
      ...(datasetMeta?.searchFields || []),
      datasetMeta?.valueField,
      ...((datasetMeta?.metadataSchema || []).map(m => m.key))
    ].filter(Boolean))
  ];

  return (
    <div style={{
      margin: '24px 16px 0', padding: '14px 16px',
      background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <strong style={{ fontSize: 14, color: '#0f172a' }}>Lookup Sync (on approval)</strong>
        {enabled && (
          <button
            type="button"
            onClick={clear}
            style={{ background: 'transparent', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 12 }}
          >
            Remove
          </button>
        )}
      </div>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
        When a request from this form is fully approved, write/update a row in a lookup dataset so future requests can find it.
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 10 }}>
        <label style={{ fontSize: 13, color: '#0f172a' }}>Dataset</label>
        <select
          value={sync?.datasetId || ''}
          onChange={(e) => {
            const id = e.target.value;
            if (!id) clear();
            else update({ datasetId: id, trigger: 'on_approved', fieldMapping: sync?.fieldMapping || {} });
          }}
          style={{ flex: 1, padding: '6px 8px' }}
        >
          <option value="">— disabled —</option>
          {datasets.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
      </div>

      {enabled && datasetMeta && (
        <>
          <div style={{ fontSize: 12, color: '#475569', marginBottom: 6 }}>
            Map each dataset column to a form field:
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {datasetKeys.map(key => (
              <React.Fragment key={key}>
                <div style={{ fontSize: 13, color: '#0f172a', alignSelf: 'center' }}>
                  {key}
                  {key === datasetMeta.valueField && <span style={{ color: '#dc2626' }}> *</span>}
                </div>
                <select
                  value={(sync.fieldMapping && sync.fieldMapping[key]) || ''}
                  onChange={(e) => {
                    const mapping = { ...(sync.fieldMapping || {}) };
                    if (!e.target.value) delete mapping[key];
                    else mapping[key] = e.target.value;
                    update({ fieldMapping: mapping });
                  }}
                  style={{ padding: '6px 8px' }}
                >
                  <option value="">— unmapped —</option>
                  <option value="__systemApprovedAt">[approved at]</option>
                  <option value="__systemRequestId">[request id]</option>
                  <option value="__systemFormId">[form id]</option>
                  {fieldOptions.map(f => (
                    <option key={f.id} value={f.id}>{f.label || f.id}</option>
                  ))}
                </select>
              </React.Fragment>
            ))}
          </div>

          <div style={{ marginTop: 12, display: 'flex', gap: 12, alignItems: 'center' }}>
            <label style={{ fontSize: 13, color: '#0f172a' }}>External id column</label>
            <select
              value={sync.externalIdField || datasetMeta.valueField}
              onChange={(e) => update({ externalIdField: e.target.value })}
              style={{ flex: 1, padding: '6px 8px' }}
            >
              {datasetKeys.map(k => <option key={k} value={k}>{k}</option>)}
            </select>
          </div>
        </>
      )}
    </div>
  );
}
